// Real in-app guided tours. Each step either asks the user to tap a real
// control (`tap`) or shows an info caption with a Next button (`info`).
//
// Strictly presentational: never toggles a real setting, never writes
// preferences, never calls permission APIs, never hits Supabase.

export type TourStep =
  | {
      kind: "tap";
      /** data-tour key on the real control the user should tap. */
      target: string;
      /** Optional route to expect afterwards (advance also fires on route match). */
      advanceOnRoute?: string;
      title: string;
      body: string;
      /** Optional event dispatched once when this step becomes active (e.g. open a sheet). */
      onEnterEvent?: string;
      /** Optional event that advances this step when received. */
      advanceOnEvent?: string;
      /** Optional event dispatched when leaving this step (cleanup, e.g. close a sheet). */
      onExitEvent?: string;
    }
  | {
      kind: "info";
      /** Optional data-tour key to highlight. Centered caption if omitted/missing. */
      target?: string;
      /** Route the user should be on for this step. */
      expectRoute?: string;
      title: string;
      body: string;
      /** Optional event dispatched once when this step becomes active (e.g. open a sheet). */
      onEnterEvent?: string;
      /** Optional event that advances this step when received. */
      advanceOnEvent?: string;
      /** Optional event dispatched when leaving this step (cleanup, e.g. close a sheet). */
      onExitEvent?: string;
    };

export type Tour = {
  id: string;
  title: string;
  steps: TourStep[];
};

export const TOURS: Record<string, Tour> = {
  "tracking-settings": {
    id: "tracking-settings",
    title: "Change tracking settings",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab in the bottom bar." },
      { kind: "info", target: "row.routeTracking", expectRoute: "/permissions",
        title: "Route tracking",
        body: "This is where you control how Roava records your route. Higher accuracy gives sharper maps; Balanced uses less battery. Use Re-check or GPS detail to adjust." },
      { kind: "info",
        title: "All set",
        body: "You can change tracking anytime from this card." },
    ],
  },
  "photo-access": {
    id: "photo-access",
    title: "Manage photo access",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab." },
      { kind: "info", target: "row.photos", expectRoute: "/permissions",
        title: "Photos & videos",
        body: "Limited means Roava only sees photos you picked. Full access lets Roava match every photo to your trip. Use Review access or Choose more photos to change." },
    ],
  },
  "upload-sync": {
    id: "upload-sync",
    title: "Upload & sync settings",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab." },
      { kind: "tap", target: "row.uploadSync", advanceOnRoute: "/upload-mode",
        title: "Open Upload & sync",
        body: "Tap the Upload & sync card to see Wi-Fi, battery, and auto-upload controls." },
      { kind: "info", expectRoute: "/upload-mode",
        title: "What lives here",
        body: "Wi-Fi only holds uploads off cellular. Pause on low battery stops uploads when your phone gets low. Auto-upload sends photos in the background." },
    ],
  },
  "pod-grouping": {
    id: "pod-grouping",
    title: "Pod grouping",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab." },
      { kind: "info", target: "row.pods", expectRoute: "/permissions",
        title: "Pod grouping",
        body: "Pods turn nearby photos into one stop on your trip. Standard is the default. Tight makes smaller stops — great for cities. Loose combines more — great for road trips." },
    ],
  },
  "family-follow": {
    id: "family-follow",
    title: "Family Follow",
    steps: [
      { kind: "tap", target: "tab.trip", advanceOnRoute: "/trip",
        title: "Open your trip",
        body: "Tap the Trip tab." },
      { kind: "info", target: "row.familyFollow", expectRoute: "/trip",
        title: "Family Follow",
        body: "Share delayed, summarised trip updates with family through a private link. Family does not get traveler or admin access. Use Create share link from this card when you're ready." },
      { kind: "info",
        title: "Reminder",
        body: "Family Follow is not a live safety tool — updates are intentionally delayed and approximate." },
    ],
  },
  "battery-offline": {
    id: "battery-offline",
    title: "Battery & offline mode",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab." },
      { kind: "tap", target: "row.uploadSync", advanceOnRoute: "/upload-mode",
        title: "Open Upload & sync",
        body: "Battery and offline behavior live in upload settings." },
      { kind: "info", expectRoute: "/upload-mode",
        title: "How offline works",
        body: "Roava queues photos and route data when you're offline or on low battery, and syncs automatically when conditions improve. Wi-Fi only and Pause on low battery give you extra control." },
    ],
  },
  "notifications": {
    id: "notifications",
    title: "Notifications",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab." },
      { kind: "tap", target: "row.notifications", advanceOnRoute: "/notifications",
        title: "Open Notifications",
        body: "Tap the Notifications card." },
      { kind: "info", expectRoute: "/notifications",
        title: "What lives here",
        body: "Essentials are the alerts most trips want on. Quiet hours mute non-urgent alerts during set hours. Devices & diagnostics surfaces sync issues without spamming you." },
    ],
  },

  // -----------------------------------------------------------------
  // Forced, hard-gated walkthrough over real screens. Launched from
  // the Welcome modal's third button and from Settings → Replay full
  // walkthrough. Each step blocks all taps except the highlighted one.
  // -----------------------------------------------------------------
  "full-walkthrough": {
    id: "full-walkthrough",
    title: "Full walkthrough",
    steps: [
      { kind: "tap", target: "tab.settings", advanceOnRoute: "/permissions",
        title: "Open Settings",
        body: "Tap the Settings tab in the bottom bar to get started." },
      { kind: "info", target: "row.routeTracking", expectRoute: "/permissions",
        title: "Route tracking",
        body: "This card controls how Roava records your route while you travel." },
      { kind: "tap", target: "btn.gpsDetail", advanceOnRoute: "/permissions",
        title: "Open GPS detail",
        body: "Tap GPS detail to see the four tracking levels." },
      { kind: "info", target: "gps.sheet", expectRoute: "/permissions",
        title: "Pick a tracking level",
        body: "Battery saver, Balanced, High detail, or Expedition. Balanced is the recommended default. Tap one to continue the tour.",
        onEnterEvent: "roava:tour:open-gps-sheet",
        advanceOnEvent: "roava:gps:preset-changed",
        onExitEvent: "roava:tour:close-gps-sheet" },
      { kind: "info", target: "row.photos", expectRoute: "/permissions",
        title: "Photos & videos",
        body: "This is where you manage which photos Roava can see — Limited or Full access." },
      { kind: "info", target: "row.pods", expectRoute: "/permissions",
        title: "Pod grouping",
        body: "Pods turn nearby photos into one stop on your trip. Tight for cities, Loose for road trips." },
      { kind: "info", target: "row.notifications", expectRoute: "/permissions",
        title: "Notifications",
        body: "Trip alerts, sync reminders, and quiet hours all live here." },
      { kind: "info", target: "row.uploadSync", expectRoute: "/permissions",
        title: "Upload & sync",
        body: "Wi-Fi only, pause on low battery, and auto-upload controls." },
      { kind: "tap", target: "tab.route", advanceOnRoute: "/tracking",
        title: "Open the Route tab",
        body: "Tap Route in the bottom bar to see your map." },
      { kind: "info", target: "tracking.map", expectRoute: "/tracking",
        title: "Your live route",
        body: "This is where you start and stop trips and watch your route appear on the map." },
      { kind: "tap", target: "tab.media", advanceOnRoute: "/library",
        title: "Open the Media tab",
        body: "Tap Media to see photos grouped by day and stop." },
      { kind: "info", expectRoute: "/library",
        title: "You're all set",
        body: "That's the full tour. You can replay it any time from Settings → Replay full walkthrough." },
    ],
  },
};
